/**
 * Date helpers for the contractor workspace. CloudSafe is a UK product, so
 * "today" and every displayed date is the Europe/London calendar date — never
 * the UTC date (which is wrong between 00:00 and 01:00 during BST) and never
 * the browser's zone (which is wrong for anyone travelling).
 */

export const TZ = 'Europe/London';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const looksIso = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}([T ]\d|$)/.test(s.trim());

const isoParts = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

/** Date (or anything `new Date()` accepts) → 'YYYY-MM-DD' in London local time. '' if invalid. */
export function toLocalISODate(d) {
  if (d == null || d === '') return '';
  if (typeof d === 'string' && DATE_ONLY.test(d.trim())) return d.trim();
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  // en-CA formats as YYYY-MM-DD.
  return isoParts.format(date);
}

/** Today's London calendar date as 'YYYY-MM-DD' — use for date inputs and "is it overdue?" checks. */
export function todayLocalISO() {
  return toLocalISODate(new Date());
}

/** Turn a date-only ISO string into a Date at noon UTC, so formatting in London never shifts the day. */
const toDate = (v) => {
  if (v instanceof Date) return v;
  if (typeof v === 'string') {
    const m = DATE_ONLY.exec(v.trim());
    if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12));
  }
  return new Date(v);
};

const dateFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, day: '2-digit', month: 'short', year: 'numeric' });
const dateTimeFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const timeFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });

/**
 * ISO / Date → "01 Oct 2026". Empty → "—". Strings that aren't ISO dates
 * (already formatted, or labels like "On site") pass through unchanged.
 */
export function formatDate(v) {
  if (v == null || v === '' || v === '—') return '—';
  if (typeof v === 'string' && !looksIso(v)) return v;
  const d = toDate(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return dateFmt.format(d);
}

/** ISO / Date → "01 Oct 2026, 09:41" (London time). */
export function formatDateTime(v) {
  if (v == null || v === '' || v === '—') return '—';
  const d = toDate(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return dateTimeFmt.format(d);
}

/** ISO / Date → "09:41" (London time). */
export function formatTime(v) {
  if (v == null || v === '') return '—';
  const d = toDate(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return timeFmt.format(d);
}

/**
 * '01/10/2026' (DD/MM/YYYY, also accepts '-' or '.' and 2-digit years) →
 * '2026-10-01'. Returns null for anything that isn't a real calendar date.
 */
export function parseUkDate(s) {
  if (s == null) return null;
  const m = /^\s*(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})\s*$/.exec(String(s));
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  let year = Number(m[3]);
  if (m[3].length === 2) year += 2000;
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** True when the given date (ISO / Date) is strictly before today in London. */
export function isPastDate(v) {
  const iso = toLocalISODate(v);
  return !!iso && iso < todayLocalISO();
}

export default formatDate;
